import qs from 'qs';
import moment from 'moment';
import api from './api';
import generateSearchRequest from '../utils/sequelize-search-builder-client';

const formatFilterValuesForILikeFilter = (filters, fields) => {
	fields.forEach((field) => {
		if (filters[field]?.iLike) {
			filters[field].iLike = `%${filters[field].iLike}%`;
		}
	});
};

const toValidMoment = (value) => {
	if (value == null || value === '' || value === '0000-00-00') {
		return null;
	}

	const parsedDate = moment(value);
	return parsedDate.isValid() ? parsedDate : null;
};

const toValidDate = (value) => {
	const parsedDate = toValidMoment(value);
	return parsedDate ? parsedDate.toDate() : null;
};

const toDateOnly = (value) => {
	if (value == null || value === '') return null;
	const dateOnly = String(value).match(/^\d{4}-\d{2}-\d{2}/)?.[0];
	return dateOnly || toValidMoment(value)?.format('YYYY-MM-DD') || null;
};

const toOptionalNumber = (value) => {
	if (value === null || value === undefined || value === '') return null;
	const number = Number(value);
	return Number.isFinite(number) ? number : null;
};

const selectClient = (client) => {
	return {
		...client,
		birthdate: toValidDate(client.birthdate),
		contractedPlanDate: toValidMoment(client.contractedPlanDate),
		createdAt: toValidDate(client.createdAt),
		dueDate: toDateOnly(client.dueDate),
		canceledAt: toValidMoment(client.canceledAt)?.toISOString() || null,
		monthlyValue: toOptionalNumber(client.monthlyValue),
		openAmount: toOptionalNumber(client.openAmount),
		daysOverdue: toOptionalNumber(client.daysOverdue),
		updatedAt: toValidDate(client.updatedAt),
	};
};

const selectClients = (clients) => (Array.isArray(clients) ? clients.map(selectClient) : []);

const SUMMARY_NUMBER_FIELDS = [
	'totalClients',
	'segmentCount',
	'adimplentCount',
	'inadimplentCount',
	'canceledCount',
	'adimplencyRate',
	'monthlyRevenue',
];

const normalizeClientSummary = (summary) => {
	if (!summary || typeof summary !== 'object') return {};

	return SUMMARY_NUMBER_FIELDS.reduce(
		(normalized, field) => {
			if (!Object.prototype.hasOwnProperty.call(summary, field)) return normalized;
			const value = toOptionalNumber(summary[field]);
			return value === null ? normalized : { ...normalized, [field]: value };
		},
		{
			...summary,
			distributionByPlan: Array.isArray(summary.distributionByPlan)
				? summary.distributionByPlan
				: [],
		},
	);
};

class ClientService {
	async getById(id) {
		return api
			.get(`/clients/${id}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectClient)
			.catch((error) => {
				console.log(
					`fail when trying to get client with id ${id} in ClientService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async syncronizeStatusClientWithIUGU(client) {
		return api
			.post('/clients/syncronize/status/with/iugu', client)
			.then((response) => response.data)
			.catch((error) => {
				console.log(
					`fail when trying to syncronize client with id ${client.id} in ClientService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	// Quais dos clientes informados estão travados para receber pacotes por
	// inadimplência (regra no backend: ClientOrderBlockService).
	async getOrderBlocks(clientIds = []) {
		if (!clientIds.length) return [];
		return api
			.get('/clients/order-blocks', {
				paramsSerializer: {
					serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
				},
				params: { clientIds },
			})
			.then((response) => response.data?.body ?? [])
			.catch((error) => {
				console.log(
					'fail when trying to get client order blocks in ClientService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getAllDependets({ page = 0, size = 10, order, filters }) {
		const settings = {
			where: {
				id: {
					as: 'id',
					type: 'like',
				},
				name: {
					as: `name`,
					//type: 'iLike',
					type: 'like',
				},
				email: {
					as: 'email',
					type: 'like',
				},
				cpf: {
					as: 'cpf',
					type: 'like',
				},
				depedent: {
					as: 'depedent',
					type: 'like',
				},
				zone: {
					as: 'zone',
					type: 'like',
				},
				status: {
					as: 'status',
					type: 'equal',
				},
				paymentMethod: {
					as: 'paymentMethod',
					type: 'equal',
				},
				'status:notLike': {
					as: 'status',
					type: 'notLike',
				},
				'status:in': {
					as: 'status',
					type: 'in',
				},
				baseName: {
					as: 'base.name',
					//type: 'iLike',
					type: 'like',
				},
				baseId: {
					as: 'baseId',
					type: 'equal',
				},
				planName: {
					as: 'planName',
					type: 'equal',
				},
				'createdAt:between': {
					as: 'createdAt',
					type: 'between',
				},
				'createdAt:lte': {
					as: 'createdAt',
					type: 'lte',
				},
				_all: {
					as: '_all',
					type: 'like',
				},
			},
			order,
		};
		const request = generateSearchRequest(filters, settings);
		formatFilterValuesForILikeFilter(request.filter, ['name', 'base.name']);
		return api
			.get('/clients/dependents', {
				paramsSerializer: {
					serialize: function (params) {
						//console.log('paramsSerializer', params);
						return qs.stringify(params, { arrayFormat: 'brackets' });
					},
				},
				params: {
					page,
					size,
					...request,
				},
			})
			.then((response) => response.data)
			.then((data) => ({ ...data, body: selectClients(data.body) }))
			.catch((error) => {
				console.log(
					'fail when trying to get clients in ClientService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getAll({ page = 0, size = 10, order, filters }) {
		const settings = {
			where: {
				id: {
					as: 'id',
					type: 'like',
				},
				name: {
					as: `name`,
					//type: 'iLike',
					type: 'like',
				},
				email: {
					as: 'email',
					type: 'like',
				},
				cpf: {
					as: 'cpf',
					type: 'like',
				},
				depedent: {
					as: 'depedent',
					type: 'like',
				},
				zone: {
					as: 'zone',
					type: 'like',
				},
				status: {
					as: 'status',
					type: 'equal',
				},
				paymentMethod: {
					as: 'paymentMethod',
					type: 'equal',
				},
				'status:notLike': {
					as: 'status',
					type: 'notLike',
				},
				'status:in': {
					as: 'status',
					type: 'in',
				},
				baseName: {
					as: 'base.name',
					//type: 'iLike',
					type: 'like',
				},
				baseId: {
					as: 'baseId',
					type: 'equal',
				},
				planId: {
					as: 'planId',
					type: 'equal',
				},
				'createdAt:between': {
					as: 'createdAt',
					type: 'between',
				},
				'createdAt:lte': {
					as: 'createdAt',
					type: 'lte',
				},
				_all: {
					as: '_all',
					type: 'like',
				},
			},
			order,
		};
		const request = generateSearchRequest(filters, settings);
		formatFilterValuesForILikeFilter(request.filter, ['name', 'base.name']);
		return api
			.get('/clients', {
				paramsSerializer: {
					serialize: function (params) {
						//console.log('paramsSerializer', params);
						return qs.stringify(params, { arrayFormat: 'brackets' });
					},
				},
				params: {
					page,
					size,
					...request,
				},
			})
			.then((response) => response.data)
			.then((data) => ({ ...data, body: selectClients(data.body) }))
			.catch((error) => {
				console.log(
					'fail when trying to get clients in ClientService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getSummary({ filters = {} } = {}) {
		const settings = {
			where: {
				status: { as: 'status', type: 'equal' },
				baseId: { as: 'baseId', type: 'equal' },
				paymentMethod: { as: 'paymentMethod', type: 'equal' },
				planName: { as: 'planName', type: 'equal' },
				_all: { as: '_all', type: 'like' },
			},
		};
		const request = generateSearchRequest(filters, settings);
		return api
			.get('/clients/summary', {
				paramsSerializer: { serialize: (params) => qs.stringify(params, { arrayFormat: 'brackets' }) },
				params: request,
			})
			.then((response) => normalizeClientSummary(response.data?.body))
			.catch((error) => {
				throw error.response?.data?.error || error;
			});
	}

	async remove(id) {
		return api
			.delete(`/clients/${id}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to remove client in ClientService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async create(clientForm) {
		console.log('createClient', clientForm);
		return api
			.post('/clients', clientForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectClient)
			.catch((error) => {
				console.log(
					'fail when trying to create client in ClientService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async update(id, clientForm) {
		return api
			.patch(`/clients/${id}`, clientForm)
			.then((response) => response.data)
			.then((data) => data.body)
			.then(selectClient)
			.catch((error) => {
				console.log(
					'fail when trying to update client in ClientService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getGenders() {
		return api
			.get('/clients/genders')
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get client genders in ClientService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getClientStatuses() {
		return api
			.get('/clients/client-statuses')
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get client statuses in ClientService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getMaritalStatuses() {
		return api
			.get('/clients/marital-statuses')
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get client marital statuses in ClientService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getPlans() {
		return api
			// /clients/plans is a legacy presentation map without the identifiers
			// required by the computed client-list planId filter. /plans/all is the
			// authenticated, admin-appropriate endpoint (also used by
			// CreateContractModal.jsx, via PlanService, for this same plan-id/name
			// lookup) — /plans is public and unauthenticated, not appropriate here.
			.get('/plans/all', { params: { page: 0, size: Number.MAX_SAFE_INTEGER } })
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get client plans in ClientService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getDeliveryPreferences() {
		return api
			.get('/clients/delivery-preferences')
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					'fail when trying to get client delivery preferences in ClientService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async downloadCsv() {
		return api
			.get('/clients/download')
			.then((response) => response.data)
			.then((data) => new Blob([data], { type: 'application/csv' }))
			.then((blob) => URL.createObjectURL(blob))
			.then((uril) => {
				let link = document.createElement('a');
				link.setAttribute('href', uril);
				link.setAttribute('download', 'clientes.csv');
				let clickEvent = new MouseEvent('click', {
					view: window,
					bubbles: true,
					cancelable: false,
				});
				link.dispatchEvent(clickEvent);
			})
			.catch((error) => {
				console.log(
					'fail when trying to download csv in ClientService.',
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}
	async getByCpf(cpf) {
		return api
			.get(`/clients/cpf/${cpf}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				if (error.response?.status === 404) return null;
				throw new Error(error.response?.data?.message || 'Erro ao buscar cliente por CPF');
			});
	}

	async getByCnpj(cnpj) {
		return api
			.get(`/clients/cnpj/${cnpj}`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				if (error.response?.status === 404) return null;
				throw new Error(error.response?.data?.message || 'Erro ao buscar cliente por CNPJ');
			});
	}

	async hasClientWithCpf(cpf, clientId) {
		return api
			.head(`/clients/cpf/${cpf}/${clientId}`)
			.then((response) => true)
			.catch((error) => {
				if (error.response?.status === 404) {
					return false;
				}
				if (error.response?.status !== 404) {
					console.log(
						`fail when trying to check if client with cpf ${cpf} exists in ClientService.`,
						error.response?.data?.error,
					);
				}
				throw error.response?.data?.error;
			});
	}

	async hasClientWithCnpj(cnpj, clientId) {
		return api
			.head(`/clients/cnpj/${cnpj}/${clientId}`)
			.then((response) => true)
			.catch((error) => {
				if (error.response?.status === 404) {
					return false;
				}
				if (error.response?.status !== 404) {
					console.log(
						`fail when trying to check if client with cnpj ${cnpj} exists in ClientService.`,
						error.response?.data?.error,
					);
				}
				throw error.response?.data?.error;
			});
	}

	async hasClientWithEmail(email, clientId) {
		return api
			.head(`/clients/email/${email}/${clientId}`)
			.then((response) => true)
			.catch((error) => {
				if (error.response?.status === 404) {
					return false;
				}
				console.log(
					`fail when trying to check if client with email ${email} exists in ClientService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getPaymentMethods(clientId) {
		return api
			.get(`/clients/${clientId}/payment-methods`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					`fail when trying to get payment methods for client ${clientId} in ClientService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async getIuguClientData(clientId) {
		return api
			.get(`/clients/${clientId}/iugu-data`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					`fail when trying to get IUGU client data for client ${clientId} in ClientService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async setDefaultPaymentMethod(clientId, paymentMethodId) {
		return api
			.patch(`/clients/${clientId}/default-payment-method`, {
				paymentMethodId,
			})
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					`fail when trying to set default payment method for client ${clientId} in ClientService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async createPaymentMethod(clientId, paymentMethodData) {
		return api
			.post(`/clients/${clientId}/payment-methods`, paymentMethodData)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					`fail when trying to create payment method for client ${clientId} in ClientService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}

	async verifyClientStatus(clientId) {
		return api
			.post(`/clients/${clientId}/verify-status`)
			.then((response) => response.data)
			.then((data) => data.body)
			.catch((error) => {
				console.log(
					`fail when trying to verify status for client ${clientId} in ClientService.`,
					error.response?.data?.error,
				);
				throw error.response?.data?.error;
			});
	}
}

const clientService = new ClientService();
export { normalizeClientSummary };
export default clientService;
